export const ManageGroupsContent = {
  pageTitle: 'Groups',
  pageDescription:
    'Operational segments for event targeting, filtering, and imports.',
  actions: {
    create: 'Create group',
    edit: 'Edit group',
    archive: 'Archive group',
    restore: 'Restore group',
    copyImportKey: 'Copy import key',
    copied: 'Copied',
    cancel: 'Cancel',
    close: 'Close',
    back: 'Back to groups',
  },
  search: {
    placeholder: 'Search name, key, description',
  },
  filters: {
    parentRoleLabel: 'Parent role',
    allRoles: 'All roles',
    status: {
      active: 'Active',
      archived: 'Archived',
      all: 'All',
    },
    archivedBanner:
      'Archived groups shown. They cannot be newly assigned or targeted.',
  },
  table: {
    group: 'Group',
    parentRole: 'Parent role',
    importKey: 'Import key',
    members: 'Members',
    status: 'Status',
    description: 'Description',
    actions: 'Actions',
    noDescription: 'No description',
  },
  counts: {
    showing: (filtered: number, total: number) =>
      `Showing ${filtered} of ${total} ${filtered === 1 ? 'group' : 'groups'}`,
    members: (count: number) => `${count}`,
    memberLabel: (count: number) => (count === 1 ? 'member' : 'members'),
  },
  emptyStates: {
    noGroups: 'No groups have been created yet.',
    noGroupsDescription:
      'Create the first group to organize members for future targeting and import workflows.',
    noFilteredGroups: 'No groups match your search or filter.',
  },
  drawer: {
    createTitle: 'Create group',
    editTitle: 'Edit group',
    createDescription:
      'Set up an active group with a stable import key generated from its name.',
    editDescription:
      'Update the group label, parent role, or description. The import key stays locked.',
    fields: {
      displayName: 'Display name',
      displayNamePlaceholder: 'Fellow - 2025',
      parentRole: 'Parent role',
      parentRolePlaceholder: 'Select a parent role',
      description: 'Description',
      descriptionPlaceholder: 'Optional notes for future admins',
      importKeyPreview: 'Import key preview',
      importKey: 'Import key',
      emptyImportKey: 'Enter a name to preview the key',
    },
    importKeyHelp:
      'Used in CSV imports and exports. Generated from the display name and cannot be changed after creation.',
    lockedImportKeyHelp:
      'Renaming the group never changes this key. CSV imports and exports keep working.',
    parentRoleHelp:
      'Determines where this group appears in admin selectors. Does not limit who can belong.',
    parentRoleWarning:
      'Changing the parent role only changes where this group appears in admin screens. Existing memberships, event references, and user roles will not change.',
    createConfirm: 'Create group',
    editConfirm: 'Save changes',
    creating: 'Creating...',
    saving: 'Saving...',
    validation: {
      missingName: 'Enter a group name.',
      missingParentRole: 'Select a parent role.',
      emptyImportKey:
        'The group name must include at least one letter or number.',
    },
  },
  toast: {
    createSuccessTitle: 'Group created',
    createSuccessDescription: (name: string) => `${name} is now active.`,
    updateSuccessTitle: 'Group updated',
    updateSuccessDescription: (name: string) => `${name} has been updated.`,
    archiveSuccessTitle: 'Group archived',
    archiveSuccessDescription: (name: string) =>
      `${name} has been archived and is no longer active.`,
    restoreSuccessTitle: 'Group restored',
    restoreSuccessDescription: (name: string) => `${name} is now active again.`,
    errorTitle: 'Group update failed',
    copySuccessTitle: 'Import key copied',
  },
  archiveDrawer: {
    archiveTitle: (name: string) => `Archive ${name}?`,
    archiveDescription: 'What will happen when you archive this group:',
    archiveBullets: [
      'Existing memberships remain — no members are removed.',
      'Existing event targets keep resolving — past and future events are unaffected.',
      'Hidden from new assignment flows — cannot be newly assigned.',
      'Hidden from new event targeting — cannot be selected for new events.',
      'CSV imports using this key will be rejected.',
      'The group can be restored at any time.',
    ],
    archiveConfirm: 'Archive group',
    archiving: 'Archiving...',
    restoreTitle: (name: string) => `Restore ${name}?`,
    restoreDescription: 'What will happen when you restore this group:',
    restoreBullets: [
      'Returns to active group selectors and targeting flows.',
      'CSV imports using this key will be accepted again.',
      'Existing memberships and event references are unchanged.',
    ],
    restoreConfirm: 'Restore group',
    restoring: 'Restoring...',
  },
  detail: {
    membersSection: (count: number) =>
      `${count} ${count === 1 ? 'member' : 'members'} · Read-only`,
    memberListEmptyState: 'No members are currently assigned to this group.',
    memberListSearchEmpty: 'No members match this search.',
    memberListSearchPlaceholder: 'Search members',
    memberListLoadError: 'Could not load members.',
    memberAssignedOn: (date: string) => `Joined ${date}`,
    membershipFooter: 'Membership edits happen on each user’s detail page.',
    statusLabel: 'Status',
    membersLabel: 'Members',
    importKeyLabel: 'Import key',
    archivedBadge: 'Archived',
    activeBadge: 'Active',
  },
};
